<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { t } from './i18n';
import { listPublicTemplates } from './api';
import type { PublicDesignTemplate } from './types';

type HostRuntime = {
  apiVersion?: number;
  designer?: {
    getContext?: () => { productId: string; parentId: number; baseUrl: string };
    commands?: { loadDesign?: (state: Record<string, unknown>) => void };
  };
  ui?: {
    notify?: (type: 'success' | 'error', message: string) => void;
    confirm?: (message: string) => Promise<boolean>;
  };
};
const host = (window as unknown as { Veyop?: HostRuntime }).Veyop;
const templates = ref<PublicDesignTemplate[]>([]);
const loading = ref(false);
const loadingMore = ref(false);
const loadingId = ref(0);
const page = ref(0);
const pages = ref(0);
const loadError = ref('');
const perPage = 12;

onMounted(() => {
  void loadTemplates(true);
});

async function loadTemplates(reset = false) {
  if (loading.value || loadingMore.value) return;
  const nextPage = reset ? 1 : page.value + 1;
  reset ? loading.value = true : loadingMore.value = true;
  loadError.value = '';
  try {
    const result = await listPublicTemplates({
      page: nextPage,
      per_page: perPage,
    }, host?.designer?.getContext?.().baseUrl ?? '');
    const rows = result.items || [];
    const merged = reset ? rows : [...templates.value, ...rows];
    templates.value = [...new Map(merged.map((item) => [item.id, item])).values()];
    page.value = Number(result.page) || nextPage;
    pages.value = Number(result.pages) || page.value;
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : t('Failed to load templates');
    host?.ui?.notify?.('error', loadError.value);
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
}

async function loadTemplate(template: PublicDesignTemplate) {
  const proceed = await (host?.ui?.confirm?.(t('Loading this template will replace your current design. Continue?')) ?? Promise.resolve(false));
  if (!proceed) return;
  loadingId.value = template.id;
  try {
    const loadDesign = host?.designer?.commands?.loadDesign;
    if (!loadDesign) throw new Error(t('Host design command is unavailable'));
    const state = { ...(template.designer_state || {}) } as Record<string, unknown>;
    const stateMeta = (state.metadata && typeof state.metadata === 'object' && !Array.isArray(state.metadata))
      ? { ...(state.metadata as Record<string, unknown>) }
      : {};
    const templateAreas = template.metadata?.authoring_area_sizes;
    if (templateAreas && !stateMeta.authoring_area_sizes) {
      stateMeta.authoring_area_sizes = templateAreas;
    }
    state.metadata = stateMeta;
    loadDesign(state);
    host?.ui?.notify?.('success', t('Template loaded'));
  } catch (e) {
    host?.ui?.notify?.('error', e instanceof Error ? e.message : t('Failed to load template'));
  } finally {
    loadingId.value = 0;
  }
}
</script>

<template>
  <div class="vptw:flex vptw:flex-col vptw:gap-4">
    <div class="vptw:flex vptw:items-center vptw:justify-between vptw:gap-2">
      <p class="vptw:m-0 vptw:text-xs vptw:text-(--vs-ink-2)">{{ t('Load a ready-made template into your design.') }}</p>
      <button type="button" class="vs-icon-btn" :aria-label="t('Refresh templates')" :title="t('Refresh templates')" @click="loadTemplates(true)">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="vptw:size-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
        </svg>
      </button>
    </div>

    <p v-if="loadError" class="vptw:rounded-xl vptw:bg-red-50 vptw:p-2 vptw:text-sm vptw:text-red-700">{{ loadError }}</p>
    <div v-if="loading" class="vptw:grid vptw:grid-cols-2 vptw:gap-2">
      <div v-for="i in 6" :key="i" class="vptw:aspect-square vptw:animate-pulse vptw:rounded-xl vptw:bg-gray-100"></div>
    </div>
    <p v-else-if="!templates.length" class="vptw:py-8 vptw:text-center vptw:text-sm vptw:text-(--vs-ink-2)">{{ t('No templates available yet.') }}</p>
    <div v-else class="vptw:grid vptw:grid-cols-2 vptw:gap-2">
      <button
        v-for="item in templates"
        :key="item.id"
        class="vptw:flex vptw:flex-col vptw:items-center vptw:gap-1.5 vptw:rounded-xl! vptw:border vptw:border-(--vs-line) vptw:bg-white vptw:p-2 vptw:transition vptw:hover:border-(--vs-accent) vptw:hover:bg-(--vs-accent-soft) vptw:disabled:opacity-60 vptw:cursor-pointer"
        :disabled="loadingId === item.id"
        @click="loadTemplate(item)"
      >
        <div class="vptw:flex vptw:aspect-square vptw:w-full vptw:items-center vptw:justify-center vptw:overflow-hidden vptw:rounded-xl vptw:bg-gray-100">
          <img v-if="item.thumbnail_url" :src="item.thumbnail_url" alt="" class="vptw:h-full vptw:w-full vptw:object-cover" loading="lazy" />
          <svg v-else class="vptw:size-8 vptw:text-(--vs-ink-3)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M7 15l3-3 2 2 4-5 3 6" /></svg>
        </div>
        <span class="vptw:w-full vptw:truncate vptw:text-center vptw:text-[11px] vptw:font-medium vptw:text-(--vs-ink-2)">
          {{ loadingId === item.id ? t('Loading...') : item.title }}
        </span>
      </button>
    </div>
    <button v-if="page < pages" type="button" :disabled="loadingMore" class="vptw:w-full vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:bg-white vptw:px-4 vptw:py-2 vptw:text-sm vptw:font-semibold vptw:text-(--vs-ink-2) vptw:cursor-pointer" @click="loadTemplates(false)">{{ loadingMore ? t('Loading...') : t('Load more') }}</button>
  </div>
</template>
